import type { ReactNode } from 'react';

import { Icon } from '@/components/ui/Icon';
import { GEAR_CELL, GEAR_SIZE, GEAR_URL, gearFrame } from '@/game/data/gearAtlas';

/**
 * The Blender render for a piece of gear or a character.
 *
 * Keys are `axe/axe_03`, `lamp/lamp_07`, `renders/jin` — the shape
 * `pack_atlas.py` emits. `forGear` and `forCharacter` build them so callers
 * never hand-write one and silently miss.
 *
 * This matters more than it looks: the shop used to draw the same axe glyph
 * for a Wood Axe and a Shwe Axe, and the hero screen the same wrench-and-person
 * glyph for all six characters. An upgrade ladder where every rung looks
 * identical is not selling anything.
 */
export function gearKeyForAxe(level: number): string {
  return `axe/axe_lv${String(level).padStart(2, '0')}`;
}

export function gearKeyForLamp(level: number): string {
  return `lamp/lamp_lv${String(level).padStart(2, '0')}`;
}

export function gearKeyForCharacter(key: string): string {
  return `renders/character_${key}_portrait`;
}

export function GearIcon({ gearKey, emoji, fallback, size = 48, className = '', alt }: {
  gearKey: string;
  /** Shown when the sheet has no art — e.g. the backpack, not yet modelled. */
  emoji?: string;
  /**
   * Preferred over `emoji`, and the direction this is migrating in: a drawn
   * `<Icon>` recolours by state and renders identically on every platform, where
   * an emoji is whatever font the OS ships. A missing render should degrade to
   * the icon set, not out of it.
   */
  fallback?: ReactNode;
  size?: number;
  className?: string;
  alt?: string;
}) {
  const frame = gearFrame(gearKey);

  if (!frame) {
    return (
      <span
        className={`inline-grid place-items-center ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.6 }}
        role={alt ? 'img' : undefined}
        aria-label={alt}
        aria-hidden={alt ? undefined : true}
      >
        {fallback ?? emoji ?? <Icon name="warning" size={size * 0.55} />}
      </span>
    );
  }

  const k = size / GEAR_CELL;

  return (
    <span
      className={`inline-block shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${GEAR_URL})`,
        backgroundPosition: `-${frame.x * k}px -${frame.y * k}px`,
        backgroundSize: `${GEAR_SIZE.width * k}px ${GEAR_SIZE.height * k}px`,
        backgroundRepeat: 'no-repeat',
      }}
      role={alt ? 'img' : undefined}
      aria-label={alt}
      aria-hidden={alt ? undefined : true}
    />
  );
}
